import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/mission", label: "Our Mission" },
  { to: "/churches", label: "Churches & Properties" },
  { to: "/support", label: "Ministry & Artist Support" },
  { to: "/directory", label: "Kingdom Business Directory" },
  { to: "/jesus-united", label: "Jesus United" },
  { to: "/connect", label: "Connect & Partner" },
  { to: "/fund", label: "Fuel the Ministry", give: true },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-ivory/90 backdrop-blur-md border-b border-gold/20 shadow-[0_4px_20px_rgba(27,42,71,0.05)]" : "bg-ivory/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <img src="/lion-logo.webp" alt="Lord Almighty lion crest" className="h-12 w-12 object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-2xl font-semibold text-navy tracking-tight">Lord Almighty</span>
              <span className="text-[11px] uppercase tracking-[0.2em] text-gold-dark">Strengthening the Body</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV.slice(1).filter((n) => !n.give).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm font-medium link-underline transition-colors ${
                    isActive ? "text-emerald" : "text-navy/80 hover:text-navy"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <Button asChild className="rounded-full bg-gold hover:bg-gold-dark text-navy px-6 shadow-md">
              <Link to="/fund" data-testid="nav-give-btn"><Heart className="mr-1.5 h-4 w-4" /> Give</Link>
            </Button>
            <Button asChild className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-6">
              <Link to="/connect" data-testid="nav-partner-btn">Partner With Us</Link>
            </Button>
          </div>

          <button
            className="xl:hidden text-navy p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-ivory border-t border-gold/20 shadow-lg" data-testid="nav-mobile-menu">
          <nav className="flex flex-col px-5 py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-gold/10 ${
                    isActive ? "text-emerald" : "text-navy"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Button asChild className="mt-4 rounded-full bg-emerald hover:bg-emerald-dark text-white">
              <Link to="/connect">Partner With Us</Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
